import { StatusCodes } from 'http-status-codes';
import { USER_API_URL } from '../constants';
import {
  AuthError,
  AuthResponseSuccess,
  ERROR_MESSAGES,
  getAuthHeaders,
  handleAuthError,
  handleAuthSuccess
} from './authenticate';
import { logger } from './logger';

type PasswordlessRequestType =
  | 'passwordless_login_request'
  | 'passwordless_login_otp_verification'
  | 'passwordless_login_email_resend'
  | 'passwordless_magic_link_confirmation';

type PasswordlessRequestBody = {
  data: {
    type: PasswordlessRequestType;
    attributes: Record<string, string | number | null | undefined>;
  };
};

type PasswordlessStatusResponse = {
  status: string;
  message: string;
};

type PasswordlessInvalidResponse = {
  status: 'invalid_or_expired';
};

type PasswordlessErrorResponse = {
  errors: AuthError[] | [];
};

export type PasswordlessRequestPayload = {
  email: string;
  bypassRackAttack?: string;
};

export type PasswordlessVerifyPayload = {
  email: string;
  otp: string;
  cartId?: string | null;
  bypassRackAttack?: string;
};

export type PasswordlessResendPayload = {
  email: string;
  bypassRackAttack?: string;
};

export type PasswordlessMagicLinkPayload = {
  emailSha: string;
  uid: string;
  cartId?: string | null;
  bypassRackAttack?: string;
};

export const PASSWORDLESS_ERROR_MESSAGES = {
  invalid_or_expired: 'The code or link is invalid or expired. Please try again.',
  rate_limited: ERROR_MESSAGES.recaptcha_failed,
  default: ERROR_MESSAGES.default
} as const;

const isPasswordlessStatusResponse = (
  response: PasswordlessStatusResponse | PasswordlessInvalidResponse | PasswordlessErrorResponse
): response is PasswordlessStatusResponse => 'status' in response && 'message' in response;

const isPasswordlessInvalidResponse = (
  response: PasswordlessStatusResponse | PasswordlessInvalidResponse | PasswordlessErrorResponse
): response is PasswordlessInvalidResponse =>
  'status' in response && response.status === 'invalid_or_expired';

const isPasswordlessErrorResponse = (
  response: PasswordlessStatusResponse | PasswordlessInvalidResponse | PasswordlessErrorResponse
): response is PasswordlessErrorResponse => 'errors' in response;

const createPasswordlessError = (code: keyof typeof PASSWORDLESS_ERROR_MESSAGES): AuthError => ({
  code,
  message: PASSWORDLESS_ERROR_MESSAGES[code]
});

const postPasswordlessRequest = async <TResponse>(
  path: string,
  body: PasswordlessRequestBody,
  bypassRackAttack?: string
) => {
  const response = await fetch(`${USER_API_URL}${path}`, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: getAuthHeaders(bypassRackAttack),
    redirect: 'follow',
    referrer: 'no-referrer',
    body: JSON.stringify(body)
  });

  if (response.status === StatusCodes.TOO_MANY_REQUESTS) {
    throw createPasswordlessError('rate_limited');
  }

  return (await response.json().catch(() => {
    logger.error(
      'PASSWORDLESS_REQUEST_FAILED',
      `Passwordless request to ${path} failed with ${response.status}.`
    );
    throw createPasswordlessError('default');
  })) as TResponse;
};

const handlePasswordlessStatusResponse = (
  response: PasswordlessStatusResponse | PasswordlessInvalidResponse | PasswordlessErrorResponse
) => {
  if (isPasswordlessStatusResponse(response)) return response;

  if (isPasswordlessInvalidResponse(response)) {
    throw createPasswordlessError('invalid_or_expired');
  }

  if (isPasswordlessErrorResponse(response) && response.errors.length > 0) {
    return handleAuthError(response.errors[0]);
  }

  throw new Error(PASSWORDLESS_ERROR_MESSAGES.default);
};

export const requestPasswordlessLogin = async ({
  email,
  bypassRackAttack
}: PasswordlessRequestPayload) => {
  const response = await postPasswordlessRequest<
    PasswordlessStatusResponse | PasswordlessErrorResponse
  >(
    '/v2/passwordless/login_requests',
    {
      data: {
        type: 'passwordless_login_request',
        attributes: {
          email
        }
      }
    },
    bypassRackAttack
  );

  return handlePasswordlessStatusResponse(response) as PasswordlessStatusResponse;
};

export const resendPasswordlessEmail = async ({
  email,
  bypassRackAttack
}: PasswordlessResendPayload) => {
  const response = await postPasswordlessRequest<
    PasswordlessStatusResponse | PasswordlessErrorResponse
  >(
    '/v2/passwordless/resend_email',
    {
      data: {
        type: 'passwordless_login_email_resend',
        attributes: {
          email
        }
      }
    },
    bypassRackAttack
  );

  return handlePasswordlessStatusResponse(response) as PasswordlessStatusResponse;
};

export const verifyPasswordlessOtp = async ({
  email,
  otp,
  cartId,
  bypassRackAttack
}: PasswordlessVerifyPayload) => {
  const response = await postPasswordlessRequest<
    AuthResponseSuccess | PasswordlessInvalidResponse | PasswordlessErrorResponse
  >(
    '/v2/passwordless/otp/verify',
    {
      data: {
        type: 'passwordless_login_otp_verification',
        attributes: {
          email,
          otp,
          cart_id: cartId
        }
      }
    },
    bypassRackAttack
  );

  if ('meta' in response && 'data' in response) return handleAuthSuccess(response);

  handlePasswordlessStatusResponse(response);
  throw new Error(PASSWORDLESS_ERROR_MESSAGES.default);
};

export const confirmPasswordlessMagicLink = async ({
  emailSha,
  uid,
  cartId,
  bypassRackAttack
}: PasswordlessMagicLinkPayload) => {
  const response = await postPasswordlessRequest<
    AuthResponseSuccess | PasswordlessInvalidResponse | PasswordlessErrorResponse
  >(
    '/v2/passwordless/magic_links/confirm',
    {
      data: {
        type: 'passwordless_magic_link_confirmation',
        attributes: {
          email_sha: emailSha,
          uid,
          cart_id: cartId
        }
      }
    },
    bypassRackAttack
  );

  if ('meta' in response && 'data' in response) return handleAuthSuccess(response);

  handlePasswordlessStatusResponse(response);
  throw new Error(PASSWORDLESS_ERROR_MESSAGES.default);
};
